/**
 * La liste des feux autour de chez soi.
 *
 * Chaque bouton porte l'identifiant de sa zone en attribut `data-`, et c'est
 * la page qui écoute les clics par délégation.
 */
import { NEARBY_KM } from '../../core/constants';
import { directionWord, formatClock } from '../../core/format';
import type { FireListEntry, NearbyFires, ZoneWithPlume } from '../../state/selectors';
import { selectFireList } from '../../state/selectors';
import { html, type Html } from '../html';

const MAX_SHOWN = 6;

function detail(entry: ZoneWithPlume, now: number): string {
  const plume = entry.plume;
  if (!plume?.toward) {
    return `${Math.round(entry.distanceKm)} km, ${directionWord(plume?.bearing ?? 0)}`;
  }
  if (plume.arrival !== null) return `fumée vers ${formatClock(plume.arrival, now)}`;
  if (plume.arrivalUnavailable === 'established') {
    return `fumée déjà là, ${Math.round(plume.alongKm ?? entry.distanceKm)} km`;
  }
  return `${Math.round(entry.distanceKm)} km, dans l'axe`;
}

function name(item: FireListEntry): string {
  const label = item.entry.zone.place?.label ?? 'Foyer non localisé';
  // Plusieurs foyers dans la même commune : on le dit plutôt que de répéter
  // deux fois la même ligne.
  return item.count > 1 ? `${label} (${item.count} foyers)` : label;
}

export function fireList(fires: NearbyFires, now: number): Html {
  const items = selectFireList(fires);
  if (items.length === 0) return html``;

  const shown = items.slice(0, MAX_SHOWN);
  const hidden = items.length - shown.length;

  return html`
    <section aria-labelledby="fires-title">
      <h3 class="section-title" id="fires-title">
        Feux à moins de ${NEARBY_KM} km
        <span class="origin"
          >${items.length === 1 ? '1 endroit' : `${items.length} endroits`}</span
        >
      </h3>
      <ul class="firelist">
        ${shown.map(
          (item) => html`
            <li>
              <button
                type="button"
                data-zone-id="${item.entry.zone.id}"
                data-toward="${String(item.entry.plume?.couldReach === true)}"
              >
                <span class="dot" aria-hidden="true"></span>
                <span class="name">${name(item)}</span>
                <span class="detail">${detail(item.entry, now)}</span>
              </button>
            </li>
          `,
        )}
      </ul>
      ${hidden > 0
        ? html`<p class="hint">
            ${hidden === 1 ? 'Un autre endroit est visible' : `${hidden} autres endroits sont visibles`}
            sur la carte.
          </p>`
        : ''}
    </section>
  `;
}
